// Verificação "a parte" — roda por conta própria quando o operador aperta
// "Verificar Nota" na tela de detalhe da NFe. NÃO participa do fluxo de
// Transmitir (ver DetalheDaNfe.tsx/handleTransmitir): só lê os dados já
// carregados na tela (+ um fetch extra do cadastro de cada produto) e devolve
// uma lista de problemas pra exibir. Se este arquivo tiver um bug, o pior
// caso é a verificação ficar errada — nunca impede ou altera a emissão real.
//
// Cobre dois tipos de problema:
//  1) Regras estáticas de formato/consistência (schema da NFe + bom senso
//     fiscal): NCM com 8 dígitos, CFOP compatível com entrada/saída, CST x
//     CSOSN de acordo com o regime da empresa, dígito verificador do GTIN,
//     campos obrigatórios vazios, e a conta ICMS = BC × alíquota.
//  2) Divergência entre o item já lançado na nota e o cadastro ATUAL do
//     produto — comum quando o produto foi corrigido (NCM/CEST/CST/EAN)
//     depois que o item já estava na nota; a SEFAZ rejeita com base no que
//     está na nota, não no cadastro.
import { ProdutosService } from '../services/api/estoque/produtos/ProdutosService';
import { ParceirosService } from '../services/api/cadastros/ParceirosService';
import { TransportadoraService } from '../services/api/cadastros/TransportadoraService';

export interface IProblemaNfe {
  severidade: 'erro' | 'aviso';
  // undefined = problema do cabeçalho da nota, não de um item específico.
  itemDescricao?: string;
  // iditens do item afetado — permite abrir o item certo pra correção direto
  // a partir do resultado da verificação (ver DialogVerificarNfe.tsx).
  iditens?: string;
  // idprodutos do produto afetado — só presente em problemas que vêm da
  // comparação com o CADASTRO do produto (ver compararItensComCadastroDeProdutos),
  // não das regras estáticas do item. Permite abrir o cadastro do produto
  // certo pra correção, além (ou em vez) do item da nota.
  idprodutos?: string;
  campo: string;
  mensagem: string;
}

export interface IItemParaValidarNfe {
  iditens: string;
  idprodutos: string;
  descricao: string;
  ncm: string;
  cfop: number | string | null;
  cst: string;
  ean: string;
  un: string;
  qtd: number;
  preco: number;
  total?: number;
  desconto?: number;
  vfrete?: number;
  vbc: number;
  picms: number;
  vicms: number;
  // Grupo ICMS-ST/redução — nem todo CST usa (ver REGRAS_ICMS_POR_CST).
  modbc: string;
  modbcst?: string;
  predbc: number;
  bcicmsst: number;
  picmsst: number;
  vicmsst: number;
  // PIS/COFINS do item — só CST 01/02 carrega percentual/base/valor (ver
  // REGRAS_PIS_COFINS_POR_CST/validarConsistenciaPisCofins abaixo — mesma
  // regra já aplicada na digitação em DialogNfe.tsx/handlecstpis).
  cstpis?: string;
  ppis?: number;
  vbcpis?: number;
  vpis?: number;
  cstcofins?: string;
  pcofins?: number;
  vbccofins?: number;
  vcofins?: number;
}

export interface ICabecalhoParaValidarNfe {
  idparceiro: string;
  // 0 = Entrada, 1 = Saída (ver BasicSelectNumber 'tpnfe' em DetalheDaNfe.tsx)
  tpnfe: number;
  modfrete: number;
  idtransportadora?: string | null;
  // Totais declarados no cabeçalho da nota — comparados contra a soma dos
  // itens (ver validarTotaisNota abaixo). SEFAZ rejeita quando o total
  // declarado não bate com o somatório, e isso não é um erro de schema (o
  // xmllint nunca pegaria), é regra de negócio.
  vprod?: number;
  vicms?: number;
  vpis?: number;
  vconfins?: number;
  vfrete?: number;
  vdesc?: number;
  // Textos do cabeçalho que vão pro XML — conferidos em verificarTextos (ver
  // problemasDeTexto abaixo).
  natureza?: string;
  nfecpl?: string;
  obscomplementar?: string;
}

export interface IProdutoAtualParaValidarNfe {
  descricao: string;
  ncm: string;
  cest: string;
  ean: string;
  cst: string;
  un: string;
  status: boolean;
  // Código/descrição ANP do produto — é o que o backend usa pra montar o
  // grupo <comb> da NFe. Sem eles, um CFOP de combustível (ver
  // CFOPS_DE_COMBUSTIVEL) é rejeitado pela SEFAZ (rejeição 660: "CFOP de
  // Combustível e não informado grupo de Combustível da NF-e").
  cprodanp: string;
  descanp: string;
  // Composição do combustível (% GLP nacional/GNn/GNi) — a SEFAZ espera que
  // some ~100% quando informado (ver validarComposicaoCombustivel abaixo).
  pglp?: number;
  pnacional?: number;
  pimportado?: number;
}

const descricaoOuGenerico = (descricao?: string) => descricao?.trim() || 'Item sem descrição';

const somenteDigitos = (valor: string) => (valor || '').replace(/\D/g, '');

// Algoritmo padrão GS1 de dígito verificador (GTIN-8/12/13/14) — pesos 3/1
// alternados a partir do dígito mais à direita do payload (sem o próprio DV).
const calcularDigitoVerificadorGtin = (payloadSemDv: string): number => {
  const digitos = payloadSemDv.split('').map(Number).reverse();
  const soma = digitos.reduce((acc, d, indice) => acc + d * (indice % 2 === 0 ? 3 : 1), 0);
  return (10 - (soma % 10)) % 10;
};

const validarGtin = (ean: string): string | null => {
  const valor = (ean || '').trim();
  if (!valor || valor.toUpperCase() === 'SEM GTIN') return null;

  const digitos = somenteDigitos(valor);
  if (digitos.length !== valor.length || ![8, 12, 13, 14].includes(digitos.length)) {
    return `código de barras "${valor}" não é um GTIN válido (8, 12, 13 ou 14 dígitos numéricos, ou "SEM GTIN")`;
  }

  const payload = digitos.slice(0, -1);
  const dvInformado = Number(digitos.slice(-1));
  const dvEsperado = calcularDigitoVerificadorGtin(payload);
  if (dvInformado !== dvEsperado) {
    return `dígito verificador do GTIN "${valor}" está incorreto (esperado ...${dvEsperado})`;
  }
  return null;
};

// CST (regime normal) x CSOSN (Simples Nacional) usam o mesmo campo mas têm
// tamanhos diferentes — 2 dígitos x 3 dígitos. crt vem de EmpresaService
// (1/2 = Simples Nacional, 3 = Regime Normal).
const validarFormatoCstPeloRegime = (cst: string, crt: number | null): string | null => {
  const digitos = somenteDigitos(cst);
  if (!digitos) return 'tributação do ICMS (CST/CSOSN) não informada';
  if (crt === null) return null; // sem o regime da empresa não dá pra cruzar

  const esperaCsosn = crt === 1 || crt === 2;
  if (esperaCsosn && digitos.length !== 3) {
    return `empresa é Simples Nacional (deveria usar CSOSN de 3 dígitos) mas o item tem "${cst}"`;
  }
  if (!esperaCsosn && digitos.length !== 2) {
    return `empresa é Regime Normal (deveria usar CST de 2 dígitos) mas o item tem "${cst}"`;
  }
  return null;
};

// O que cada CST (regime normal, 2 dígitos) / CSOSN (Simples Nacional, 3
// dígitos — nunca colidem em tamanho, então cabem na mesma tabela) permite ou
// exige no grupo ICMS do item. Isso é o que já está codificado à mão em
// DialogNfe.tsx (ex.: handlecst zera modBC quando o CST vira '60' — "não
// existe base de cálculo pra escolher o modo"), só que aqui formalizado numa
// tabela pra poder VERIFICAR uma nota já lançada, não só bloquear na digitação.
interface IRegraIcmsCst {
  // true = ICMS próprio não se aplica (isento/já tributado/diferido) — modBC
  // deve ficar vazio e BC/alíquota/valor do ICMS próprio devem ser zero.
  proprioIcmsNaoSeAplica?: boolean;
  // true = espera-se BC/alíquota/valor de ICMS-ST preenchidos.
  exigeSubstituicaoTributaria?: boolean;
  // true = espera-se percentual de redução de BC preenchido.
  exigeReducaoBc?: boolean;
}

const REGRAS_ICMS_POR_CST: Record<string, IRegraIcmsCst> = {
  // CST — Regime Normal
  '00': {},
  '10': { exigeSubstituicaoTributaria: true },
  '20': { exigeReducaoBc: true },
  '30': { proprioIcmsNaoSeAplica: true, exigeSubstituicaoTributaria: true },
  '40': { proprioIcmsNaoSeAplica: true },
  '41': { proprioIcmsNaoSeAplica: true },
  '50': { proprioIcmsNaoSeAplica: true },
  '60': { proprioIcmsNaoSeAplica: true },
  '70': { exigeReducaoBc: true, exigeSubstituicaoTributaria: true },
  '90': {}, // "Outras" — deliberadamente flexível, sem regra forte
  // CSOSN — Simples Nacional
  '101': { proprioIcmsNaoSeAplica: true },
  '102': { proprioIcmsNaoSeAplica: true },
  '103': { proprioIcmsNaoSeAplica: true },
  '201': { proprioIcmsNaoSeAplica: true, exigeSubstituicaoTributaria: true },
  '202': { proprioIcmsNaoSeAplica: true, exigeSubstituicaoTributaria: true },
  '203': { proprioIcmsNaoSeAplica: true, exigeSubstituicaoTributaria: true },
  '300': { proprioIcmsNaoSeAplica: true },
  '400': { proprioIcmsNaoSeAplica: true },
  '500': { proprioIcmsNaoSeAplica: true },
  '900': {}, // "Outros" — flexível
};

const indicaSubstituicaoTributaria = (cst: string) => !!REGRAS_ICMS_POR_CST[somenteDigitos(cst)]?.exigeSubstituicaoTributaria;

// O caso que deu origem a essa checagem: CST 60 (ICMS já cobrado por ST
// antes) com Mod. BC preenchida — não existe BC própria nesse CST, então
// modBC não deveria ter valor (ver handlecst em DialogNfe.tsx, mesma regra).
const validarConsistenciaIcmsPorCst = (item: IItemParaValidarNfe): string[] => {
  const digitos = somenteDigitos(item.cst);
  const regra = REGRAS_ICMS_POR_CST[digitos];
  if (!regra) return []; // CST desconhecido/não mapeado — já é reportado por validarFormatoCstPeloRegime

  const mensagens: string[] = [];
  const rotulo = `CST/CSOSN ${digitos}`;

  if (regra.proprioIcmsNaoSeAplica) {
    if (item.modbc) {
      mensagens.push(`${rotulo} não tem base de cálculo própria de ICMS, mas Mod. BC está preenchida ("${item.modbc}").`);
    }
    if (item.vbc > 0 || item.picms > 0 || item.vicms > 0) {
      mensagens.push(`${rotulo} não tributa ICMS próprio, mas BC/alíquota/valor do ICMS estão preenchidos (BC ${item.vbc}, alíq. ${item.picms}%, valor ${item.vicms}).`);
    }
  } else if (!item.modbc) {
    mensagens.push(`${rotulo} normalmente exige a Modalidade de determinação da BC do ICMS (Mod. BC) preenchida.`);
  }

  if (regra.exigeReducaoBc && item.predbc <= 0) {
    mensagens.push(`${rotulo} indica redução de base de cálculo, mas o percentual de redução está zerado.`);
  }

  if (regra.exigeSubstituicaoTributaria) {
    if (item.bcicmsst <= 0 && item.vicmsst <= 0) {
      mensagens.push(`${rotulo} indica ICMS-ST, mas a BC ST e o valor do ICMS-ST estão zerados.`);
    }
    if (!item.modbcst) {
      mensagens.push(`${rotulo} indica ICMS-ST, mas a Modalidade de BC do ICMS-ST (Mod. BC ST) não foi informada.`);
    }
  } else if (item.bcicmsst > 0 || item.vicmsst > 0) {
    mensagens.push(`${rotulo} não indica ICMS-ST, mas há BC ST/valor de ICMS-ST preenchidos (BC ${item.bcicmsst}, valor ${item.vicmsst}).`);
  }

  return mensagens;
};

// PIS/COFINS: só CST 01/02 (Tributável com alíquota básica/diferenciada)
// carrega percentual + base + valor — qualquer outro CST (isento, alíquota
// zero, substituição, "outras operações"...) tem que estar com esses três
// campos zerados. Mesma regra que handlecstpis/handlecstcofins já aplicam
// na digitação em DialogNfe.tsx — aqui formalizada pra poder VERIFICAR uma
// nota já lançada (inclusive uma lançada antes desse fix existir).
const CST_PIS_COFINS_COM_PERCENTUAL = ['01', '02'];

const validarConsistenciaPisCofins = (item: IItemParaValidarNfe): string[] => {
  const mensagens: string[] = [];

  const cstpis = somenteDigitos(item.cstpis || '').padStart(2, '0');
  if (cstpis !== '00') {
    const comPercentualPis = CST_PIS_COFINS_COM_PERCENTUAL.includes(cstpis);
    const ppis = Number(item.ppis) || 0;
    const vbcpis = Number(item.vbcpis) || 0;
    const vpis = Number(item.vpis) || 0;
    if (comPercentualPis && (ppis <= 0 || vbcpis <= 0)) {
      mensagens.push(`CST do PIS "${cstpis}" exige percentual e base de cálculo do PIS preenchidos (percentual ${ppis}%, BC ${vbcpis}).`);
    } else if (!comPercentualPis && (ppis !== 0 || vbcpis !== 0 || vpis !== 0)) {
      mensagens.push(`CST do PIS "${cstpis}" não deveria ter percentual/base/valor de PIS preenchidos (percentual ${ppis}%, BC ${vbcpis}, valor ${vpis}).`);
    }
  }

  const cstcofins = somenteDigitos(item.cstcofins || '').padStart(2, '0');
  if (cstcofins !== '00') {
    const comPercentualCofins = CST_PIS_COFINS_COM_PERCENTUAL.includes(cstcofins);
    const pcofins = Number(item.pcofins) || 0;
    const vbccofins = Number(item.vbccofins) || 0;
    const vcofins = Number(item.vcofins) || 0;
    if (comPercentualCofins && (pcofins <= 0 || vbccofins <= 0)) {
      mensagens.push(`CST do COFINS "${cstcofins}" exige percentual e base de cálculo do COFINS preenchidos (percentual ${pcofins}%, BC ${vbccofins}).`);
    } else if (!comPercentualCofins && (pcofins !== 0 || vbccofins !== 0 || vcofins !== 0)) {
      mensagens.push(`CST do COFINS "${cstcofins}" não deveria ter percentual/base/valor de COFINS preenchidos (percentual ${pcofins}%, BC ${vbccofins}, valor ${vcofins}).`);
    }
  }

  return mensagens;
};

// SEFAZ rejeita quando o total declarado no cabeçalho da nota não bate com
// o somatório dos itens — não é erro de schema (xmllint nunca pegaria), é
// conta de regra de negócio. Tolerância de 2 centavos (ou 1% do total,
// o que for maior) pra não pegar diferença de arredondamento inofensiva.
const proximoDeZero = (diferenca: number, tolerancia: number) => Math.abs(diferenca) <= tolerancia;

const validarTotaisNota = (cabecalho: ICabecalhoParaValidarNfe, itens: IItemParaValidarNfe[]): IProblemaNfe[] => {
  const problemas: IProblemaNfe[] = [];
  const somar = (campo: (item: IItemParaValidarNfe) => number | undefined) =>
    itens.reduce((acc, item) => acc + (Number(campo(item)) || 0), 0);

  const checagens: Array<[string, number, number | undefined]> = [
    ['Valor dos Produtos', somar((i) => i.total), cabecalho.vprod],
    ['Valor do ICMS', somar((i) => i.vicms), cabecalho.vicms],
    ['Valor do PIS', somar((i) => i.vpis), cabecalho.vpis],
    ['Valor do COFINS', somar((i) => i.vcofins), cabecalho.vconfins],
    ['Valor do Frete', somar((i) => i.vfrete), cabecalho.vfrete],
    ['Valor do Desconto', somar((i) => i.desconto), cabecalho.vdesc],
  ];
  for (const [campo, somaItens, totalCabecalho] of checagens) {
    if (totalCabecalho === undefined) continue; // cabeçalho não mandou esse total — não dá pra comparar
    const total = Number(totalCabecalho) || 0;
    const tolerancia = Math.max(0.02, total * 0.01);
    if (!proximoDeZero(somaItens - total, tolerancia)) {
      problemas.push({
        severidade: 'erro',
        campo,
        mensagem: `${campo} da nota (${total.toFixed(2)}) não bate com a soma dos itens (${somaItens.toFixed(2)}).`,
      });
    }
  }
  return problemas;
};

// Tabela CFOP — "Operações com combustíveis ou lubrificantes" (grupos
// 1.65x/2.65x/3.65x na entrada, 5.65x/6.65x/7.65x na saída). Quando o item
// usa um desses, a NFe precisa do grupo <comb> (Código/Descrição ANP do
// produto) — sem isso a SEFAZ rejeita com o código 660 ("CFOP de Combustível
// e não informado grupo de Combustível da NF-e").
const CFOPS_DE_COMBUSTIVEL = new Set([
  '1651', '1652', '1653', '1658', '1659', '1660', '1661', '1662', '1663',
  '2651', '2652', '2653', '2658', '2659', '2660', '2661', '2662', '2663',
  '3651', '3652', '3653',
  '5651', '5652', '5653', '5654', '5655', '5656', '5657', '5658', '5659', '5660', '5661', '5662', '5663', '5664', '5665', '5666',
  '6651', '6652', '6653', '6654', '6655', '6656', '6657', '6658', '6659', '6660', '6661', '6662', '6663', '6664', '6665', '6666',
  '7651', '7654', '7658', '7660',
]);

const CFOP_POR_DIRECAO: Record<number, string[]> = {
  0: ['1', '2', '3'], // Entrada
  1: ['5', '6', '7'], // Saída
};

export const validarRegrasEstaticasNfe = (
  cabecalho: ICabecalhoParaValidarNfe,
  itens: IItemParaValidarNfe[],
  crt: number | null,
): IProblemaNfe[] => {
  const problemas: IProblemaNfe[] = [];

  if (!cabecalho.idparceiro) {
    problemas.push({ severidade: 'erro', campo: 'Destinatário', mensagem: 'Nenhum destinatário/parceiro selecionado.' });
  }
  if (itens.length === 0) {
    problemas.push({ severidade: 'erro', campo: 'Itens', mensagem: 'A nota não tem nenhum item lançado.' });
  }
  if (cabecalho.modfrete !== 9 && !cabecalho.idtransportadora) {
    problemas.push({
      severidade: 'aviso',
      campo: 'Transportadora',
      mensagem: 'Tipo de frete indica transporte, mas nenhuma transportadora foi selecionada.',
    });
  }

  for (const item of itens) {
    const nome = descricaoOuGenerico(item.descricao);
    const add = (severidade: IProblemaNfe['severidade'], campo: string, mensagem: string) =>
      problemas.push({ severidade, itemDescricao: nome, iditens: item.iditens, campo, mensagem });

    if (!item.descricao?.trim()) add('erro', 'Descrição', 'Item sem descrição.');

    const ncmDigitos = somenteDigitos(item.ncm);
    if (!ncmDigitos) {
      add('erro', 'NCM', 'NCM não informado.');
    } else if (ncmDigitos.length !== 8) {
      add('erro', 'NCM', `NCM "${item.ncm}" deveria ter 8 dígitos.`);
    }

    const cfopStr = item.cfop != null ? String(item.cfop) : '';
    const cfopDigitos = somenteDigitos(cfopStr);
    if (!cfopDigitos) {
      add('erro', 'CFOP', 'CFOP não informado.');
    } else if (cfopDigitos.length !== 4) {
      add('erro', 'CFOP', `CFOP "${cfopStr}" deveria ter 4 dígitos.`);
    } else {
      const direcaoEsperada = CFOP_POR_DIRECAO[cabecalho.tpnfe];
      if (direcaoEsperada && !direcaoEsperada.includes(cfopDigitos[0])) {
        add('erro', 'CFOP', `CFOP "${cfopStr}" não é compatível com ${cabecalho.tpnfe === 1 ? 'Saída' : 'Entrada'} (deveria começar com ${direcaoEsperada.join(', ')}).`);
      }
    }

    const erroCst = validarFormatoCstPeloRegime(item.cst, crt);
    if (erroCst) add('erro', 'CST/CSOSN', `${erroCst[0].toUpperCase()}${erroCst.slice(1)}.`);

    // Inconsistência entre o CST/CSOSN escolhido e os campos do grupo ICMS
    // preenchidos no item (ex.: CST 60 com Mod. BC preenchida) — ver
    // REGRAS_ICMS_POR_CST/validarConsistenciaIcmsPorCst acima.
    for (const mensagem of validarConsistenciaIcmsPorCst(item)) {
      add('erro', 'ICMS', mensagem);
    }

    // Mesma ideia, pro grupo PIS/COFINS (ver REGRAS_PIS_COFINS_POR_CST/
    // validarConsistenciaPisCofins acima).
    for (const mensagem of validarConsistenciaPisCofins(item)) {
      add('erro', 'PIS/COFINS', mensagem);
    }

    const erroGtin = validarGtin(item.ean);
    if (erroGtin) add('aviso', 'GTIN/EAN', `${erroGtin[0].toUpperCase()}${erroGtin.slice(1)}.`);

    if (item.qtd <= 0) add('erro', 'Quantidade', `Quantidade inválida (${item.qtd}).`);
    if (item.preco < 0) add('erro', 'Valor unitário', `Valor unitário negativo (${item.preco}).`);
    if (item.preco === 0) add('aviso', 'Valor unitário', 'Valor unitário zerado — confirme se é intencional (brinde/amostra).');

    if (item.picms > 0 && item.vbc > 0) {
      const icmsCalculado = Number((item.vbc * (item.picms / 100)).toFixed(2));
      const diferenca = Math.abs(icmsCalculado - item.vicms);
      if (diferenca > 0.05) {
        add('aviso', 'ICMS', `Valor do ICMS (${item.vicms.toFixed(2)}) diverge do calculado a partir da BC × alíquota (${icmsCalculado.toFixed(2)}).`);
      }
    }
  }

  // Totais só fazem sentido conferir se já não faltou item — soma de uma
  // lista vazia contra um cabeçalho com total > 0 só duplicaria o aviso de
  // "sem itens" lá de cima.
  if (itens.length > 0) {
    problemas.push(...validarTotaisNota(cabecalho, itens));
  }

  return problemas;
};

export const compararItensComCadastroDeProdutos = (
  itens: IItemParaValidarNfe[],
  produtosPorId: Map<string, IProdutoAtualParaValidarNfe>,
): IProblemaNfe[] => {
  const problemas: IProblemaNfe[] = [];

  for (const item of itens) {
    const produto = produtosPorId.get(item.idprodutos);
    if (!produto) continue; // produto pode ter sido excluído — não é o foco aqui
    const nome = descricaoOuGenerico(item.descricao);
    const add = (severidade: IProblemaNfe['severidade'], campo: string, mensagem: string) =>
      problemas.push({ severidade, itemDescricao: nome, iditens: item.iditens, idprodutos: item.idprodutos, campo, mensagem });

    if (!produto.status) {
      add('aviso', 'Cadastro do Produto', 'Produto está INATIVO no cadastro atual.');
    }
    if (somenteDigitos(produto.ncm) && somenteDigitos(produto.ncm) !== somenteDigitos(item.ncm)) {
      add('aviso', 'NCM', `Item usa NCM "${item.ncm}", mas o cadastro atual do produto tem NCM "${produto.ncm}".`);
    }
    // Erro, não aviso: CST/CSOSN é o que decide se o ICMS é destacado, isento,
    // já retido por ST etc. — um item com CST 60 (ICMS cobrado antes por ST,
    // vICMS = 0) enquanto o cadastro atual do produto é 00 (tributação
    // integral) não é uma diferença cosmética, é o item saindo com o ICMS
    // errado (não calculado quando deveria ter sido, ou vice-versa).
    if (produto.cst && somenteDigitos(produto.cst) !== somenteDigitos(item.cst)) {
      add('erro', 'CST/CSOSN', `Item usa tributação "${item.cst}", mas o cadastro atual do produto tem "${produto.cst}" — o ICMS do item pode estar calculado com a regra errada.`);
    }
    if (produto.ean && produto.ean.toUpperCase() !== 'SEM GTIN' && produto.ean !== item.ean) {
      add('aviso', 'GTIN/EAN', `Item usa código de barras "${item.ean}", mas o cadastro atual do produto tem "${produto.ean}".`);
    }
    if (produto.un && produto.un !== item.un) {
      add('aviso', 'Unidade', `Item usa unidade "${item.un}", mas o cadastro atual do produto tem "${produto.un}".`);
    }

    const cfopDigitos = somenteDigitos(item.cfop != null ? String(item.cfop) : '');
    if (CFOPS_DE_COMBUSTIVEL.has(cfopDigitos) && !produto.cprodanp?.trim()) {
      add('erro', 'CFOP', `CFOP "${cfopDigitos}" é de operação com combustível/lubrificante, mas o produto não tem o Código ANP cadastrado — a SEFAZ rejeita a nota por faltar o grupo de combustível (rejeição 660).`);
    }
    // Cobre o outro caso de combustível: REVENDA (posto/varejo), que usa CFOP
    // comum de venda (não entra em CFOPS_DE_COMBUSTIVEL, que é só pra
    // distribuidora/refinaria) — o sinal aqui é o CST 61 do ICMS
    // (tributação monofásica sobre combustíveis, Convênio ICMS 199/2022),
    // que exige o mesmo grupo <comb>/Código ANP no XML. Olha o CST ATUAL do
    // cadastro do produto (produto.cst), não o do item já lançado — é o
    // cadastro que decide se a tag vai ou não entrar na hora de emitir de
    // verdade (ver GeraNfe.ts/tagComb, backend).
    const cstAtualCombustivel = somenteDigitos(produto.cst || item.cst) === '61';
    if (cstAtualCombustivel && !produto.cprodanp?.trim()) {
      add('erro', 'Combustível (ANP)', `CST do ICMS "61" (revenda de combustível, tributação monofásica) exige o Código ANP cadastrado no produto — sem ele a nota sai com a tag cProdANP vazia e a validação de schema rejeita ao transmitir.`);
    }
    // Caso real que originou esta checagem: um produto comum (nem
    // combustível, CST 60) com cprodanp = " " (só espaço) no cadastro —
    // "espaço" é truthy em JS, então passava pelo `if (cProdANP)` do backend
    // (GeraNfe.ts/tagComb) e o XML saía com <cProdANP> </cProdANP>
    // vazio-mas-não-vazio, rejeitado pela validação de schema na hora de
    // transmitir, sem apontar qual produto. Não depende de CST/CFOP — é
    // sujeira de cadastro que pode acontecer em QUALQUER produto (alguém
    // clicou no campo ANP e saiu sem digitar nada de verdade).
    if (produto.cprodanp && !produto.cprodanp.trim()) {
      add('erro', 'Combustível (ANP)', `Código ANP do produto está preenchido só com espaço(s) em branco — isso quebra a transmissão da nota (o XML sai com o campo vazio). Abra o cadastro do produto, botão "Dados de Combustível", e limpe o campo (deixe realmente vazio) ou preencha com o código de verdade.`);
    }
    if (produto.cprodanp?.trim()) {
      const soma = (Number(produto.pglp) || 0) + (Number(produto.pnacional) || 0) + (Number(produto.pimportado) || 0);
      if (soma > 0 && Math.abs(soma - 100) > 0.5) {
        add('aviso', 'Combustível (ANP)', `Soma dos percentuais de composição do combustível (${soma.toFixed(2)}%) deveria ser 100%.`);
      }
    }

    const cstAtualDigitos = somenteDigitos(produto.cst || item.cst);
    if (indicaSubstituicaoTributaria(cstAtualDigitos) && !somenteDigitos(produto.cest)) {
      add('erro', 'CEST', 'Tributação indica ICMS-ST, mas o produto não tem CEST cadastrado.');
    }
  }

  return problemas;
};

// Busca o cadastro atual de cada produto ÚNICO citado nos itens — uma
// chamada por produto (dedup por idprodutos), disparadas em paralelo.
export const buscarProdutosAtuaisParaValidacao = async (
  itens: IItemParaValidarNfe[],
): Promise<Map<string, IProdutoAtualParaValidarNfe>> => {
  const idsUnicos = Array.from(new Set(itens.map((item) => item.idprodutos).filter(Boolean)));
  const resultados = await Promise.all(
    idsUnicos.map(async (id) => {
      const produto = await ProdutosService.getById(id);
      return [id, produto] as const;
    }),
  );

  const mapa = new Map<string, IProdutoAtualParaValidarNfe>();
  for (const [id, produto] of resultados) {
    if (produto instanceof Error) continue; // produto sumiu/erro de rede — ignora, não quebra a verificação
    mapa.set(id, {
      descricao: produto.descricao,
      ncm: produto.ncm,
      cest: produto.cest,
      ean: produto.ean,
      cst: produto.cst,
      cprodanp: produto.cprodanp,
      descanp: produto.descanp,
      pglp: Number(produto.pglp) || 0,
      pnacional: Number(produto.pnacional) || 0,
      pimportado: Number(produto.pimportado) || 0,
      un: produto.un,
      status: produto.status,
    });
  }
  return mapa;
};

// --- Textos que vão pro XML ------------------------------------------------
// Problemas de texto que a SEFAZ rejeita SEM dizer onde estão:
//  - espaço no começo/fim (ex.: GTIN " SEM GTIN"): a nota era assinada com o
//    espaço e enviada sem ele → rejeição 297 "Assinatura difere do
//    calculado" (o backend passou a limpar isso antes de assinar, mas é
//    sujeira de cadastro que vale corrigir na origem);
//  - quebra de linha/tabulação e caracteres de controle invisíveis;
//  - caracteres fora do que o leiaute da NF-e aceita (só até "ÿ", Latin-1):
//    travessão "–", aspas curvas “ ”, reticências "…", "€", emoji... — vêm de
//    copiar/colar do Word/WhatsApp e dão rejeição de schema.
// Só espaço comum aqui — quebra de linha/tab e espaço "especial" (não
// separável etc.) têm mensagem própria logo abaixo.
const ESPACO_NAS_PONTAS = /^ | $/;
const QUEBRA_OU_TAB = /[\r\n\t]/;
// Montado por código (e não escrito literalmente) pra não deixar caractere
// invisível no próprio código-fonte: controles ASCII (menos tab/quebra, já
// tratados acima), DEL, espaço não separável, larguras zero e BOM.
const CODIGOS_INVISIVEIS = [
  ...Array.from({ length: 32 }, (_, codigo) => codigo).filter((codigo) => ![9, 10, 13].includes(codigo)),
  0x7f, 0xa0, 0x200b, 0x200c, 0x200d, 0xfeff,
];
const temCaractereInvisivel = (valor: string) => Array.from(valor).some((caractere) => CODIGOS_INVISIVEIS.includes(caractere.codePointAt(0)!));

const problemasDeTexto = (valor: unknown, opcoes: { permitirQuebraDeLinha?: boolean } = {}): string[] => {
  if (typeof valor !== 'string' || valor === '') return [];
  const problemas: string[] = [];
  if (ESPACO_NAS_PONTAS.test(valor)) {
    const onde = /^ /.test(valor) && / $/.test(valor) ? 'no começo e no fim' : /^ /.test(valor) ? 'no começo' : 'no fim';
    problemas.push(valor.trim() === '' ? 'está preenchido só com espaço(s) em branco' : `tem espaço ${onde} ("${valor}") — pode causar a rejeição 297 (assinatura difere do calculado)`);
  }
  if (!opcoes.permitirQuebraDeLinha && QUEBRA_OU_TAB.test(valor)) {
    problemas.push(/^[\r\n\t]|[\r\n\t]$/.test(valor)
      ? 'tem quebra de linha (Enter) ou tabulação no começo/fim — pode causar a rejeição 297 (assinatura difere do calculado)'
      : 'tem quebra de linha (Enter) ou tabulação no meio do texto');
  }
  if (temCaractereInvisivel(valor)) problemas.push('tem um caractere invisível (copiado de outro programa) — apague e digite o texto de novo');
  // Os invisíveis acima já têm mensagem própria — não repete aqui.
  const foraDoLeiaute = Array.from(new Set(Array.from(valor).filter((caractere) =>
    caractere.codePointAt(0)! > 0xff && !CODIGOS_INVISIVEIS.includes(caractere.codePointAt(0)!))));
  if (foraDoLeiaute.length) problemas.push(`tem caractere que a NF-e não aceita: ${foraDoLeiaute.map((c) => `"${c}"`).join(', ')}`);
  return problemas;
};

const conferirCampos = (
  campos: Array<[string, unknown, { permitirQuebraDeLinha?: boolean }?]>,
  registrar: (campo: string, problema: string) => void,
) => {
  for (const [nome, valor, opcoes] of campos) {
    for (const problema of problemasDeTexto(valor, opcoes)) registrar(nome, problema);
  }
};

export const verificarTextos = (
  cabecalho: ICabecalhoParaValidarNfe,
  itens: IItemParaValidarNfe[],
  produtosPorId: Map<string, IProdutoAtualParaValidarNfe>,
  cliente?: Record<string, unknown> | null,
  transportadora?: Record<string, unknown> | null,
): IProblemaNfe[] => {
  const problemas: IProblemaNfe[] = [];
  const maiuscula = (texto: string) => `${texto[0].toUpperCase()}${texto.slice(1)}.`;

  conferirCampos([
    ['Natureza da operação', cabecalho.natureza],
    ['Informações complementares', cabecalho.nfecpl, { permitirQuebraDeLinha: true }],
    ['Observação complementar', cabecalho.obscomplementar, { permitirQuebraDeLinha: true }],
  ], (campo, problema) => problemas.push({ severidade: 'erro', campo: `Nota — ${campo}`, mensagem: maiuscula(problema) }));

  if (cliente) {
    conferirCampos([
      ['Nome', cliente.nome], ['CPF/CNPJ', cliente.cpf], ['Inscrição Estadual', cliente.rg],
      ['Endereço', cliente.endereco], ['Número', cliente.numero], ['Bairro', cliente.bairro],
      ['Complemento', cliente.complemento], ['CEP', cliente.cep], ['Telefone', cliente.telefone], ['E-mail', cliente.email],
    ], (campo, problema) => problemas.push({
      severidade: 'erro', campo: `Cadastro do Cliente (${String(cliente.nome || '').trim() || 'destinatário'}) — ${campo}`, mensagem: maiuscula(problema),
    }));
  }

  if (transportadora) {
    conferirCampos([
      ['Nome', transportadora.nome], ['CPF/CNPJ', transportadora.cpf], ['Inscrição Estadual', transportadora.rg],
      ['Endereço', transportadora.endereco],
    ], (campo, problema) => problemas.push({
      severidade: 'erro', campo: `Cadastro da Transportadora (${String(transportadora.nome || '').trim()}) — ${campo}`, mensagem: maiuscula(problema),
    }));
  }

  for (const item of itens) {
    const nome = descricaoOuGenerico(item.descricao);
    // O que está gravado no item da nota (abre o item pra corrigir)...
    conferirCampos([
      ['Descrição', item.descricao], ['Código de barras (GTIN)', item.ean], ['Unidade', item.un],
    ], (campo, problema) => problemas.push({
      severidade: 'erro', itemDescricao: nome, iditens: item.iditens, campo: `Item da nota — ${campo}`, mensagem: maiuscula(problema),
    }));
    // ...e o cadastro do produto, que também vai pro XML (CEST, ANP) e é a
    // origem do problema quando o item copiou de lá (abre o cadastro).
    const produto = produtosPorId.get(item.idprodutos);
    if (!produto) continue;
    conferirCampos([
      ['Descrição', produto.descricao], ['Código de barras (GTIN)', produto.ean], ['Unidade', produto.un],
      ['CEST', produto.cest], ['Descrição ANP', produto.descanp],
    ], (campo, problema) => problemas.push({
      severidade: 'erro', itemDescricao: nome, iditens: item.iditens, idprodutos: item.idprodutos, campo: `Cadastro do Produto — ${campo}`, mensagem: maiuscula(problema),
    }));
  }

  // Item e cadastro com o MESMO texto sujo viram dois avisos iguais — fica
  // só o do cadastro (corrigindo lá, o item é corrigido ao relançar).
  return problemas.filter((p) => !(p.campo.startsWith('Item da nota') && problemas.some((outro) =>
    outro.iditens === p.iditens && outro.campo === p.campo.replace('Item da nota', 'Cadastro do Produto') && outro.mensagem === p.mensagem)));
};

// Cadastros do cliente e da transportadora — só pra conferir os textos
// (verificarTextos). Erro de rede não quebra a verificação.
const buscarCadastro = async <T,>(buscar: () => Promise<T | Error>): Promise<Record<string, unknown> | null> => {
  try {
    const resultado = await buscar();
    return resultado instanceof Error ? null : (resultado as unknown as Record<string, unknown>);
  } catch {
    return null;
  }
};

// Orquestra as etapas — é isso que a tela chama no clique do botão.
export const verificarNotaFiscalAntesDeEnviar = async (
  cabecalho: ICabecalhoParaValidarNfe,
  itens: IItemParaValidarNfe[],
  crt: number | null,
): Promise<IProblemaNfe[]> => {
  const problemasEstaticos = validarRegrasEstaticasNfe(cabecalho, itens, crt);
  const [produtosAtuais, cliente, transportadora] = await Promise.all([
    buscarProdutosAtuaisParaValidacao(itens),
    cabecalho.idparceiro ? buscarCadastro(() => ParceirosService.getById(cabecalho.idparceiro)) : Promise.resolve(null),
    cabecalho.idtransportadora ? buscarCadastro(() => TransportadoraService.getById(String(cabecalho.idtransportadora))) : Promise.resolve(null),
  ]);
  const problemasDivergencia = compararItensComCadastroDeProdutos(itens, produtosAtuais);
  const problemasTexto = verificarTextos(cabecalho, itens, produtosAtuais, cliente, transportadora);
  return [...problemasEstaticos, ...problemasTexto, ...problemasDivergencia];
};
